import { useCallback, useState } from 'react';
import { SUCCESS_TIMEOUT } from '../../config/modal-config';
import useSearchState from './useSearchState';

const INITIAL_STATE = {
    isOpen: false,
    entryType: null,
    mealId: null,
    viewStack: [],
    viewPayload: null,
    action: null,
    pickerMode: false,
    onPickItems: null
};

const useModalController = () => {
    const [state, setState] = useState(INITIAL_STATE);

    const search = useSearchState();

    // OPEN / CLOSE

    const open = useCallback(
        ({ entryType = null, mealId = null }, initialView = 'default', action = null, options = {}) => {
            setState({
                isOpen: true,
                entryType,
                mealId,
                viewStack: [initialView],
                viewPayload: options?.payload ?? null,
                action,
                pickerMode: options?.pickerMode ?? false,
                onPickItems: options?.onPickItems ?? null
            });
        },
        []
    );

    const close = useCallback(() => {
        search.onResetSearch();
        setState(INITIAL_STATE);
    }, [search]);

    const closeWithDelay = useCallback(
        (delay) => {
            const timer = setTimeout(close, delay ?? SUCCESS_TIMEOUT);
            return () => clearTimeout(timer);
        },
        [close]
    );

    // NAVIGATION

    const setView = useCallback((nextView, payload = null) => {
        setState((prev) => ({
            ...prev,
            viewStack: [...prev.viewStack, nextView],
            viewPayload: payload
        }));
    }, []);

    const replaceView = useCallback((nextView, payload = null) => {
        setState((prev) => {
            if (prev.viewStack.length === 0) return prev;

            return {
                ...prev,
                viewStack: [...prev.viewStack.slice(0, -1), nextView],
                viewPayload: payload
            };
        });
    }, []);

    const goBack = useCallback(() => {
        setState((prev) => {
            if (prev.viewStack.length <= 1) return prev;

            return {
                ...prev,
                viewStack: prev.viewStack.slice(0, -1),
                viewPayload: null
            };
        });
    }, []);

    // STATE MUTATORS

    const setEntryType = useCallback((entryType) => {
        setState((prev) => ({
            ...prev,
            entryType
        }));
    }, []);

    const setAction = useCallback((action) => {
        setState((prev) => ({
            ...prev,
            action
        }));
    }, []);

    // DERIVED

    const view = state.viewStack[state.viewStack.length - 1] ?? null;

    return {
        isOpen: state.isOpen,
        entryType: state.entryType,
        mealId: state.mealId,
        action: state.action,
        search,
        pickerMode: state.pickerMode,
        onPickItems: state.onPickItems,
        view,
        viewPayload: state.viewPayload,
        canGoBack: state.viewStack.length > 1,
        open,
        close,
        closeWithDelay,
        setView,
        replaceView,
        goBack,
        setEntryType,
        setAction
    };
};

export default useModalController;
